<script setup>
import { ref, inject } from 'vue';
import { useMainStore } from '../../../../stores/index';
import { AxiosAPI } from '@/axios';

const props = defineProps({
  ExistCheckList: {
    type: Array,
    default: () => []
  },
  TaskID: {
    type: Number,
    required: true
  }
});

const Store = useMainStore();
const emitter = inject('emitter');

const lists = ref(props.ExistCheckList ? [...props.ExistCheckList] : []);
const newItem = ref({
  description: '',
  is_checked: false,
  isEditingItem: false
});
const loading = ref(false);

const addList = () => {
  loading.value = true;
  const Items = JSON.stringify([{ description: 'Nova tarefa', is_checked: false, isEditingItem: false }]);
  const formData = new FormData();
  formData.append('title', 'Lista de tarefas');
  formData.append('isEditingText', 'false');
  formData.append('items', Items);

  AxiosAPI.post(`/api/tasks/${props.TaskID}/checklists/`, formData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Store.accessToken}`
    }
  }).then((resp) => {
    const newChecklistId = resp.data.checklist.id;
    lists.value.push({
      id: newChecklistId,
      title: 'Lista de tarefas',
      isEditingText: false,
      items: [
        {
          description: '',
          is_checked: false,
          isEditingItem: true
        }
      ],
      addItemInput: false
    });
    if (emitter) emitter.emit('KanbanUpdated');
  }).finally(() => {
    loading.value = false;
  });
};

const addItem = (listIndex) => {
  if (!newItem.value.description.trim()) return;
  lists.value[listIndex].items.push({ ...newItem.value });
  const checklistItems = JSON.stringify(lists.value[listIndex].items);
  const formData = new FormData();
  formData.append('title', lists.value[listIndex].title);
  formData.append('items', checklistItems);
  formData.append('_method', 'PUT');

  AxiosAPI.post(`/api/tasks/${props.TaskID}/checklists/${lists.value[listIndex].id}`, formData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Store.accessToken}`
    }
  }).then(() => {
    newItem.value = {
      description: '',
      isEditingItem: false,
      is_checked: false
    };
    if (emitter) emitter.emit('KanbanUpdated');
  });
};

const editTitle = (index) => {
  lists.value[index].isEditingText = true;
};

const saveTitle = (index, checklistID) => {
  lists.value[index].isEditingText = false;
  const checklistItems = JSON.stringify(lists.value[index].items);
  const formData = new FormData();
  formData.append('title', lists.value[index].title);
  formData.append('items', checklistItems);
  formData.append('_method', 'PUT');

  AxiosAPI.post(`/api/tasks/${props.TaskID}/checklists/${checklistID}`, formData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Store.accessToken}`
    }
  }).then(() => {
    if (emitter) emitter.emit('KanbanUpdated');
  });
};

const editItem = (listIndex, itemIndex) => {
  lists.value[listIndex].items[itemIndex].isEditingItem = true;
};

const saveItemTitle = (listIndex, itemIndex, checklistID) => {
  lists.value[listIndex].items[itemIndex].isEditingItem = false;

  const checklistItems = JSON.stringify(lists.value[listIndex].items);
  const formData = new FormData();
  formData.append('title', lists.value[listIndex].title);
  formData.append('items', checklistItems);
  formData.append('_method', 'PUT');

  AxiosAPI.post(`/api/tasks/${props.TaskID}/checklists/${checklistID}`, formData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Store.accessToken}`
    }
  }).then(() => {
    if (emitter) emitter.emit('KanbanUpdated');
  });
};

const CheckInput = (listIndex, itemIndex, checklistID) => {
  setTimeout(() => {
    const checklistItems = JSON.stringify(lists.value[listIndex].items);
    const formData = new FormData();
    formData.append('title', lists.value[listIndex].title);
    formData.append('items', checklistItems);
    formData.append('_method', 'PUT');

    AxiosAPI.post(`/api/tasks/${props.TaskID}/checklists/${checklistID}`, formData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${Store.accessToken}`
      }
    }).then(() => {
      if (emitter) emitter.emit('KanbanUpdated');
    });
  }, 200);
};

const removeItem = (listIndex, itemIndex, checklistID) => {
  lists.value[listIndex].items.splice(itemIndex, 1);

  const checklistItems = JSON.stringify(lists.value[listIndex].items);
  const formData = new FormData();
  formData.append('title', lists.value[listIndex].title);
  formData.append('items', checklistItems);
  formData.append('_method', 'PUT');

  AxiosAPI.post(`/api/tasks/${props.TaskID}/checklists/${checklistID}`, formData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Store.accessToken}`
    }
  }).then(() => {
    if (emitter) emitter.emit('KanbanUpdated');
  });
};

const removeList = (checklistID) => {
  lists.value = lists.value.filter(l => l.id !== checklistID);
  AxiosAPI.delete(`/api/tasks/${props.TaskID}/checklists/${checklistID}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Store.accessToken}`
    }
  }).then(() => {
    if (emitter) emitter.emit('KanbanUpdated');
  });
};

const calculateProgress = (list) => {
  if (!list.items || list.items.length === 0) return 0;
  const completedItems = list.items.filter(item => item.is_checked).length;
  return Math.round((completedItems / list.items.length) * 100);
};

const ItemsChecked = (list) => {
  if (!list.items || list.items.length === 0) return 0;
  return list.items.filter(item => item.is_checked).length;
};

defineExpose({
  lists
});
</script>

<template>
  <div class="todolist-container">
    <div v-for="(list, index) in lists" :key="list.id || index" class="checklist-block mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center flex-grow-1 mr-2">
          <i class="fa-solid fa-list-check text-muted mr-2" style="font-size: 16px;"></i>
          <span
            v-if="!list.isEditingText"
            class="font-weight-bold text-dark cursor-pointer"
            style="font-size: 14px;"
            @click="editTitle(index)"
          >
            {{ list.title || 'Lista' }}
          </span>

          <input
            v-else
            v-model="list.title"
            class="title-input"
            @blur="saveTitle(index, list.id)"
            @keyup.enter="saveTitle(index, list.id)"
            type="text"
          />
        </div>

        <button type="button" class="btn-remove-icon" @click="removeList(list.id)" title="Excluir lista">
          <i class="fa-regular fa-trash-can"></i>
        </button>
      </div>

      <!-- Barra de progresso -->
      <div class="progress-bar-wrapper mb-1">
        <div class="progress-bar-fill" :style="{ width: calculateProgress(list) + '%' }"></div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="text-muted" style="font-size: 12px;">Progresso</span>
        <span class="text-muted" style="font-size: 12px;">{{ ItemsChecked(list) }}/{{ list.items ? list.items.length : 0 }}</span>
      </div>

      <!-- Input para novo item -->
      <div v-if="list.addItemInput" class="mb-2">
        <input
          type="text"
          v-model="newItem.description"
          placeholder="Digite o item e pressione Enter..."
          class="item-input"
          @keyup.enter="addItem(index); list.addItemInput = false"
          @blur="list.addItemInput = false"
        />
      </div>

      <!-- Itens da lista -->
      <div class="items-list">
        <div
          v-for="(item, itemIndex) in list.items"
          :key="itemIndex"
          class="checklist-item d-flex align-items-center justify-content-between p-2 mb-1"
        >
          <div class="d-flex align-items-center flex-grow-1 pr-2">
            <input
              type="checkbox"
              v-model="item.is_checked"
              class="custom-checkbox mr-2"
              @change="CheckInput(index, itemIndex, list.id)"
            />

            <span
              v-if="!item.isEditingItem"
              :class="['item-text', item.is_checked ? 'item-text--completed' : '']"
              @click="editItem(index, itemIndex)"
            >
              {{ item.description }}
            </span>

            <input
              v-else
              v-model="item.description"
              @blur="saveItemTitle(index, itemIndex, list.id)"
              @keyup.enter="saveItemTitle(index, itemIndex, list.id)"
              type="text"
              class="item-inline-input"
            />
          </div>

          <button type="button" class="btn-remove-icon" @click="removeItem(index, itemIndex, list.id)" title="Remover item">
            <i class="fa-regular fa-trash-can"></i>
          </button>
        </div>
      </div>

      <button type="button" class="btn-add-item mt-2" @click="list.addItemInput = true">
        <i class="fa-solid fa-plus mr-1"></i> Adicionar item
      </button>
    </div>

    <button
      type="button"
      class="btn-outline-action"
      @click="addList"
      :disabled="loading"
    >
      <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
      Adicionar lista de tarefas
    </button>
  </div>
</template>

<style scoped>
.checklist-block {
  background-color: #ffffff;
}

.title-input {
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 2px 6px;
  outline: none;
  width: 100%;
}

.btn-remove-icon {
  background: transparent;
  border: none;
  color: #212529;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 14px;
  opacity: 0.7;
  transition: opacity 0.15s;
}

.btn-remove-icon:hover {
  opacity: 1;
  color: #dc3545;
}

.progress-bar-wrapper {
  width: 100%;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background-color: #F1860B;
  border-radius: 4px;
  transition: width 0.25s ease;
}

.checklist-item {
  background-color: #f8f9fa;
  border-radius: 6px;
  transition: background-color 0.15s;
}

.checklist-item:hover {
  background-color: #f1f3f5;
}

.custom-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #F1860B;
  cursor: pointer;
}

.item-text {
  font-size: 13px;
  color: #212529;
  cursor: pointer;
}

.item-text--completed {
  text-decoration: line-through;
  color: #6c757d;
}

.item-inline-input,
.item-input {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  outline: none;
}

.btn-add-item {
  background: transparent;
  border: none;
  color: #F1860B;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.btn-add-item:hover {
  color: #d97405;
  text-decoration: underline;
}

.btn-outline-action {
  color: #F1860B;
  border: 1px solid #F1860B;
  background: transparent;
  font-weight: 600;
  font-size: 13px;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-outline-action:hover {
  background-color: rgba(241, 134, 11, 0.08);
}
</style>